import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import styled from "styled-components";
import { getBaseMapStyle } from "../utils/mapStyles";
import POIMarkers from "./POIMarkers";
import POIPopup from "./POIPopup";
import { addGeoJSONLayers, fitBounds } from "../utils/mapRenderUtils";

const MapContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
`;

export const StyledMarker = styled.div`
  width: 12px;
  height: 12px;
  background: ${(props) => props.color || "#005fcc"};
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
`;

const PopupContent = styled.div`
  padding: 10px;
  min-width: 200px;
`;

const Input = styled.input`
  width: 100%;
  margin-bottom: 8px;
  padding: 4px;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  margin-bottom: 8px;
  padding: 4px;
`;

const Button = styled.button`
  width: 100%;
  padding: 4px;
  background: #005fcc;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #004999;
  }
`;

const PopupContainer = styled.div`
  position: absolute;
  z-index: 10;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 220px;
  pointer-events: auto;
`;

export default function MapView({
  basemap,
  geojsonData,
  hoverLocation,
  showMarkers,
  selectedRegionId,
  visualizationSettings,
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const popupRef = useRef(null);
  const hoverMarkerRef = useRef(null);
  const clickHandlerRef = useRef(null);
  const styleLoadingRef = useRef(false);
  const prevSelectedRegionId = useRef();
  const [markerScreenPositions, setMarkerScreenPositions] = useState([]);
  const [newPOIPosition, setNewPOIPosition] = useState(null);
  const [selectedRegionPOIId, setSelectedRegionPOIId] = useState(null);
  const [selectedRegionPOI, setSelectedRegionPOI] = useState(null);
  const [regionPOIs, setRegionPOIs] = useState([]);

  // Add escape key handler
  const handleKeyDown = (e) => {
    if (e.key === "Escape" && popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  };
  window.addEventListener("keydown", handleKeyDown);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    // Clean up existing map if it exists
    if (mapRef.current) {
      try {
        // Check if the map is still valid before removing
        if (mapRef.current.getContainer() && !mapRef.current._removed) {
          mapRef.current.remove();
        }
      } catch (error) {
        console.warn("Error removing existing map:", error);
      }
      mapRef.current = null;
    }

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getBaseMapStyle(basemap),
      center: [0, 0],
      pitch: 0,
      bearing: 0,
      maxZoom: 16,
      interactive: true,
    });

    mapRef.current = map;
    styleLoadingRef.current = true;

    // Handle style loading
    const handleStyleLoad = () => {
      styleLoadingRef.current = false;

      // Add terrain for MapTiler basemap
      if (basemap === "maptiler") {
        // Add the DEM source
        map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "https://api.maptiler.com/tiles/terrain-rgb/tiles.json?key=HXgK2fqnmExz8Z5kpMyR",
          tileSize: 512,
          maxzoom: 14,
        });

        // Set terrain after source is added
        map.setTerrain({
          source: "mapbox-dem",
          exaggeration: 1,
        });
      }

      if (geojsonData.length > 0) {
        addGeoJSONLayers(map, geojsonData, visualizationSettings);
      }
    };

    map.on("style.load", handleStyleLoad);

    // Add error handling
    map.on("error", (e) => {
      console.error("Map error:", e);
    });

    return () => {
      if (mapRef.current) {
        try {
          // Check if the map is still valid before cleaning up
          if (mapRef.current.getContainer() && !mapRef.current._removed) {
            mapRef.current.off("style.load", handleStyleLoad);
            mapRef.current.remove();
          }
        } catch (error) {
          console.warn("Error cleaning up map:", error);
        }
        mapRef.current = null;
      }
    };
  }, [basemap]);

  // click handler for new POI creation
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedRegionId) return;

    // Create new click handler
    clickHandlerRef.current = (mapClickEvent) => {
      const point = map.project(mapClickEvent.lngLat);
      setNewPOIPosition({
        lng: mapClickEvent.lngLat.lng,
        lat: mapClickEvent.lngLat.lat,
        x: point.x,
        y: point.y,
      });
    };

    // Add new click handler
    map.on("click", clickHandlerRef.current);

    // Cleanup
    return () => {
      if (clickHandlerRef.current) {
        map.off("click", clickHandlerRef.current);
      }
    };
  }, [selectedRegionId, basemap]);

  // hover and highlighted track handler
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clean up existing marker if no hover location
    if (!hoverLocation) {
      if (hoverMarkerRef.current) {
        hoverMarkerRef.current.remove();
        hoverMarkerRef.current = null;
      }

      // Hide highlighted track layer if it exists
      if (map.getLayer("highlighted-track")) {
        map.setLayoutProperty("highlighted-track", "visibility", "none");
      }
      return;
    }

    // Handle highlighted track segments (not a simple marker)
    if (hoverLocation.segments) {
      if (map.getLayer("highlighted-track")) {
        const segmentIds = hoverLocation.segments.map(
          (segment) => segment.properties.id
        );
        map.setFilter("highlighted-track", [
          "in",
          ["get", "id"],
          ["literal", segmentIds],
        ]);
        map.setLayoutProperty("highlighted-track", "visibility", "visible");
      }
      return;
    }

    // Handle simple location marker
    if (hoverLocation.lng && hoverLocation.lat) {
      // Create marker if it doesn't exist
      if (!hoverMarkerRef.current) {
        const el = document.createElement("div");
        createRoot(el).render(<StyledMarker />);
        hoverMarkerRef.current = new maplibregl.Marker({ element: el })
          .setLngLat([hoverLocation.lng, hoverLocation.lat])
          .addTo(map);
      } else {
        // Update existing marker position
        hoverMarkerRef.current.setLngLat([
          hoverLocation.lng,
          hoverLocation.lat,
        ]);
      }
    }
  }, [hoverLocation]);

  // Hide new POI popup when markers are toggled
  useEffect(() => {
    setNewPOIPosition(null);
  }, [showMarkers]);

  // Handle GeoJSON data
  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return;
    }
    if (geojsonData.length === 0) {
      return;
    }
    if (styleLoadingRef.current) {
      return;
    }

    // Function to add layers once everything is ready
    const addLayersWhenReady = () => {
      if (styleLoadingRef.current) {
        return;
      }
      if (!map.isStyleLoaded()) {
        return;
      }

      // Only update if the source data has changed
      const currentSource = map.getSource("gpx-tracks");
      if (currentSource) {
        try {
          currentSource.setData({
            type: "FeatureCollection",
            features: geojsonData,
          });

          // Update layer visibility
          if (map.getLayer("hiking-tracks")) {
            map.setLayoutProperty("hiking-tracks", "visibility", "visible");
          }
        } catch (error) {
          console.error("Error updating source:", error);
        }
        // Only call fitBounds if selectedRegion has just changed
        if (prevSelectedRegionId.current !== selectedRegionId) {
          fitBounds(map, geojsonData, visualizationSettings);
        }
        prevSelectedRegionId.current = selectedRegionId;
        return;
      }

      addGeoJSONLayers(map, geojsonData, visualizationSettings);
    };

    addLayersWhenReady();
  }, [geojsonData, selectedRegionId]); // Only data and region changes

  // Calculate marker screen positions on map move/resize
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !regionPOIs.length) {
      setMarkerScreenPositions([]);
      return;
    }
    function updatePositions() {
      setMarkerScreenPositions(
        Object.fromEntries(
          regionPOIs.map((poi) => {
            const lngLat = poi.coordinates;
            const point = map.project(lngLat);
            return [poi.id, { id: poi.id, x: point.x, y: point.y, poi }];
          })
        )
      );
    }
    updatePositions();
    map.on("move", updatePositions);
    map.on("resize", updatePositions);
    return () => {
      map.off("move", updatePositions);
      map.off("resize", updatePositions);
    };
  }, [regionPOIs, basemap]);

  // Dismiss selected poi popup on map click
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const closePopup = () => {
      setSelectedRegionPOIId(null);
    };
    map.on("click", closePopup);
    return () => {
      map.off("click", closePopup);
    };
  }, []);

  // Modify the basemap change effect to handle terrain updates
  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleLoadingRef.current) return;

    // Remove existing terrain if any
    if (map.getTerrain()) {
      map.setTerrain(null);
    }

    // Add terrain for MapTiler basemap
    if (basemap === "maptiler") {
      // Add the DEM source if it doesn't exist
      if (!map.getSource("mapbox-dem")) {
        map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "https://api.maptiler.com/tiles/terrain-rgb/tiles.json?key=HXgK2fqnmExz8Z5kpMyR",
          tileSize: 512,
          maxzoom: 14,
        });
      }

      // Set terrain after source is added
      map.setTerrain({
        source: "mapbox-dem",
        exaggeration: 1.5,
      });
    }
  }, [basemap]);

  return (
    <MapContainer ref={mapContainerRef}>
      {showMarkers && (
        <POIMarkers
          markerScreenPositions={markerScreenPositions}
          setSelectedRegionPOIId={setSelectedRegionPOIId}
        />
      )}
      {selectedRegionId ? (
        <POIPopup
          selectedRegionId={selectedRegionId}
          setSelectedRegionPOIId={setSelectedRegionPOIId}
          marker={markerScreenPositions[selectedRegionId]}
          setRegionPOIs={setRegionPOIs}
          regionPOIs={regionPOIs}
        />
      ) : null}
      {newPOIPosition && !selectedRegionId && showMarkers && (
        <NewPOIPopup
          selectedRegionId={selectedRegionId}
          position={newPOIPosition}
          setRegionPOIs={setRegionPOIs}
          onClose={() => setNewPOIPosition(null)}
        />
      )}
    </MapContainer>
  );
}
